/** The automatic send's state: on/off and last run (per browser), last result (per tab). */
import { useSyncExternalStore } from "react";
import type { IHandoverAutoResult } from "./handoverAutoPlan";

const ENABLED_KEY = "isyn-handover-auto-enabled";
const LAST_RUN_KEY = "isyn-handover-auto-lastrun";

const safe = <T,>(f: () => T, fallback: T): T => { try { return f(); } catch { return fallback; } };

export interface IHandoverAutoState {
  enabled: boolean;
  running: boolean;
  result: IHandoverAutoResult | null;
}

/** OFF unless somebody switched it on in this browser. */
const readEnabled = (): boolean => safe(() => localStorage.getItem(ENABLED_KEY) === "1", false);

export const readHandoverLastRun = (): number =>
  safe(() => Number(localStorage.getItem(LAST_RUN_KEY)) || 0, 0);
export const writeHandoverLastRun = (at: number): void =>
  safe(() => localStorage.setItem(LAST_RUN_KEY, String(at)), undefined);

let state: IHandoverAutoState = { enabled: readEnabled(), running: false, result: null };
const listeners = new Set<() => void>();
const emit = (next: IHandoverAutoState): void => { state = next; listeners.forEach((l) => l()); };

export const getHandoverAutoState = (): IHandoverAutoState => state;
export const setHandoverAutoEnabled = (enabled: boolean): void => {
  safe(() => localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0"), undefined);
  emit({ ...state, enabled });
};
export const setHandoverAutoRunning = (running: boolean): void => emit({ ...state, running });
export const recordHandoverAutoResult = (result: IHandoverAutoResult): void => emit({ ...state, result });

export const useHandoverAutoState = (): IHandoverAutoState =>
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    getHandoverAutoState, getHandoverAutoState,
  );
